import React, { useState, useRef } from 'react';
import { STATUS_OPTIONS, LM_BOX_BULK_OPTIONS } from '../../utils/constants';

/**
 * Table Primitives
 *
 * Shared building blocks for any data table in the portal.
 *
 * Exports:
 *   TableHeader  — sticky header that swaps to inline bulk actions when rows are selected
 *   SkeletonRow  — animated loading placeholder row
 *   RecordRow    — selectable, expandable table row wrapper
 *   Pagination   — page controls + rows-per-page picker
 */

// ── TableHeader ───────────────────────────────────────────────────────────────
/**
 * TableHeader
 *
 * Sticky table header with two modes:
 *   1. Normal — renders column labels with sort indicators and a checkbox
 *   2. Bulk   — when selectedIds.size > 0, replaces column labels with
 *               inline action controls (status, letter made, interaction
 *               logged, life box, notes, export, clear)
 *
 * The select-all-filtered banner appears as a second header row when needed.
 *
 * Props:
 *   columns               — array of { label, field, className } for normal mode
 *   selectedIds           — Set of selected row IDs
 *   filteredCount         — total rows matching current filters
 *   allSelected            — true when "select all N filtered" triggered
 *   pageAllChecked         — whether all rows on this page are checked
 *   pageIndeterminate      — whether some (not all) page rows are checked
 *   onTogglePage           — fn() — toggle all rows on current page
 *   onSelectAllFiltered    — fn() — select all filtered rows
 *   onClearSelection       — fn() — clear all selected rows
 *   onBulkStatus           — fn(status)
 *   onBulkLetterMade       — fn(bool) — set LetterMade on all targeted records
 *   onBulkInteractionLogged— fn(bool) — set TEDInteractionLogged on all targeted records
 *   onBulkLifeBoxStatus    — fn('Sent' | 'Pending') — set LifeMemberBoxStatus on all targeted records
 *   onBulkNotes            — fn(text)
 *   onExportCSV            — fn()
 *   onGenerateLetters      — fn() | null
 *   bulkProgress           — null | { done, total, failed, phase }
 *   sortField              — current sort field
 *   sortDir                — 'asc' | 'desc'
 *   onSort                 — fn(field)
 */
export function TableHeader({
  columns = [],
  selectedIds,
  filteredCount,
  allSelected,
  pageAllChecked,
  pageIndeterminate,
  onTogglePage,
  onSelectAllFiltered,
  onClearSelection,
  onBulkStatus,
  onBulkLetterMade,
  onBulkInteractionLogged,
  onBulkLifeBoxStatus,
  onBulkNotes,
  onExportCSV,
  onGenerateLetters = null,
  bulkProgress,
  sortField,
  sortDir,
  onSort,
}) {
  const [pendingStatus,      setPendingStatus]      = useState('');
  const [pendingLetterMade,  setPendingLetterMade]  = useState('');
  const [pendingInteraction, setPendingInteraction] = useState('');
  const [pendingLifeBox,     setPendingLifeBox]     = useState('');
  const [pendingNote,        setPendingNote]        = useState('');
  const [noteOpen,           setNoteOpen]           = useState(false);

  const count  = selectedIds.size;
  const active = count > 0;
  const busy   = bulkProgress !== null;

  function handleApplyStatus() {
    if (!pendingStatus || busy) return;
    onBulkStatus(pendingStatus);
    setPendingStatus('');
  }

  function handleApplyLetterMade() {
    if (!pendingLetterMade || busy) return;
    onBulkLetterMade(pendingLetterMade === 'Made');
    setPendingLetterMade('');
  }

  function handleApplyInteraction() {
    if (!pendingInteraction || busy) return;
    onBulkInteractionLogged(pendingInteraction === 'Logged');
    setPendingInteraction('');
  }

  function handleApplyLifeBox() {
    if (!pendingLifeBox || busy) return;
    onBulkLifeBoxStatus(pendingLifeBox);
    setPendingLifeBox('');
  }

  function handleApplyNote() {
    const note = pendingNote.trim();
    if (!note || busy) return;
    onBulkNotes(note);
    setPendingNote('');
    setNoteOpen(false);
  }

  // Close all pending bulk inputs when selection cleared
  React.useEffect(() => {
    if (!active) {
      setNoteOpen(false);
      setPendingNote('');
      setPendingStatus('');
      setPendingLetterMade('');
      setPendingInteraction('');
      setPendingLifeBox('');
    }
  }, [active]);

  const thBase = 'px-3 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider whitespace-nowrap select-none';

  // Shared class for every small dropdown in the bulk bar, so the six
  // clusters read as one consistent control language rather than six
  // separately-styled widgets.
  const bulkSelectClass = 'h-7 text-xs border border-[var(--color-border)] rounded-[var(--radius-md)] px-2 bg-white text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] disabled:opacity-50 disabled:cursor-not-allowed';
  const bulkApplyClass  = 'h-7 px-2.5 text-xs font-semibold rounded-[var(--radius-md)] bg-[var(--color-orange)] text-white hover:bg-[var(--color-orange-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  const bulkDivider     = <div className="h-3.5 w-px bg-[var(--color-border)] shrink-0" />;

  return (
    <thead className="bg-[var(--color-surface-alt)] border-b border-[var(--color-border)] sticky top-0 z-10">

      {/* ── Main header row ── */}
      <tr>
        {/* Checkbox cell — always present */}
        <th className="px-3 py-3 w-10">
          <input
            type="checkbox"
            checked={pageAllChecked}
            ref={el => { if (el) el.indeterminate = pageIndeterminate; }}
            onChange={onTogglePage}
            className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-orange)] cursor-pointer"
          />
        </th>

        {active ? (
          /* ── Bulk action mode — spans all remaining columns ── */
          <th colSpan={columns.length + 1} className="px-2 py-2">
            <div className="flex items-center gap-2 flex-wrap">

              {/* Count */}
              <span className="text-xs font-semibold text-[var(--color-text-primary)] shrink-0 mr-1">
                {count} selected
              </span>

              {bulkDivider}

              {/* Status picker */}
              <div className="flex items-center gap-1.5 shrink-0">
                <select
                  value={pendingStatus}
                  onChange={e => setPendingStatus(e.target.value)}
                  disabled={busy}
                  className={bulkSelectClass}
                >
                  <option value="">Set status…</option>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={handleApplyStatus} disabled={!pendingStatus || busy} className={bulkApplyClass}>
                  Apply
                </button>
              </div>

              {bulkDivider}

              {/* Letter Made picker */}
              <div className="flex items-center gap-1.5 shrink-0">
                <select
                  value={pendingLetterMade}
                  onChange={e => setPendingLetterMade(e.target.value)}
                  disabled={busy}
                  className={bulkSelectClass}
                >
                  <option value="">Letter made…</option>
                  <option value="Made">Mark Made</option>
                  <option value="Not Made">Mark Not Made</option>
                </select>
                <button onClick={handleApplyLetterMade} disabled={!pendingLetterMade || busy} className={bulkApplyClass}>
                  Apply
                </button>
              </div>

              {bulkDivider}

              {/* Interaction Logged picker */}
              <div className="flex items-center gap-1.5 shrink-0">
                <select
                  value={pendingInteraction}
                  onChange={e => setPendingInteraction(e.target.value)}
                  disabled={busy}
                  className={bulkSelectClass}
                >
                  <option value="">Interaction…</option>
                  <option value="Logged">Mark Logged</option>
                  <option value="Not Logged">Mark Not Logged</option>
                </select>
                <button onClick={handleApplyInteraction} disabled={!pendingInteraction || busy} className={bulkApplyClass}>
                  Apply
                </button>
              </div>

              {bulkDivider}

              {/* Life Member Box picker — bulk limited to Sent/Pending only;
                  Hold and Boxed stay per-record via DetailsPanel. */}
              <div className="flex items-center gap-1.5 shrink-0">
                <select
                  value={pendingLifeBox}
                  onChange={e => setPendingLifeBox(e.target.value)}
                  disabled={busy}
                  className={bulkSelectClass}
                >
                  <option value="">LM Box…</option>
                  {LM_BOX_BULK_OPTIONS.map(v => <option key={v} value={v}>{`Mark ${v}`}</option>)}
                </select>
                <button onClick={handleApplyLifeBox} disabled={!pendingLifeBox || busy} className={bulkApplyClass}>
                  Apply
                </button>
              </div>

              {bulkDivider}

              {/* Append note */}
              {noteOpen ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <input
                    value={pendingNote}
                    onChange={e => setPendingNote(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleApplyNote()}
                    autoFocus
                    placeholder="Note to append…"
                    className="h-7 w-44 text-xs border border-[var(--color-border)] rounded-[var(--radius-md)] px-2 bg-white focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] placeholder-[var(--color-text-muted)]"
                  />
                  <button
                    onClick={handleApplyNote}
                    disabled={!pendingNote.trim() || busy}
                    className={bulkApplyClass}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setNoteOpen(false); setPendingNote(''); }}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setNoteOpen(true)}
                  disabled={busy}
                  className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  Append note…
                </button>
              )}

              {bulkDivider}

              {/* Export */}
              <button
                onClick={onExportCSV}
                disabled={busy}
                className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                Export CSV
              </button>

              {bulkDivider}

              {/* Generate letters */}
              <span
                title={onGenerateLetters ? undefined : 'Coming soon'}
                className={[
                  'text-xs shrink-0',
                  onGenerateLetters
                    ? 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] cursor-pointer transition-colors'
                    : 'text-[var(--color-text-muted)] opacity-40 cursor-not-allowed',
                ].join(' ')}
                onClick={onGenerateLetters && !busy ? onGenerateLetters : undefined}
              >
                Generate Letters
              </span>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Progress or clear */}
              {busy ? (
                <span className="text-xs text-[var(--color-text-muted)] shrink-0">
                  {bulkProgress.phase === 'running'
                    ? `Saving ${bulkProgress.done} of ${bulkProgress.total}…`
                    : bulkProgress.phase === 'done'
                      ? `✓ ${bulkProgress.total} updated`
                      : `${bulkProgress.done - bulkProgress.failed} saved, ${bulkProgress.failed} failed`}
                </span>
              ) : (
                <button
                  onClick={onClearSelection}
                  className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors shrink-0"
                >
                  ✕ Clear
                </button>
              )}
            </div>

            {/* Progress bar — thin strip below the action row */}
            {busy && (
              <div className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-[var(--color-border)]">
                <div
                  className={[
                    'h-full rounded-full transition-all duration-300',
                    bulkProgress.phase === 'done'  ? 'bg-[var(--color-success)]' :
                    bulkProgress.phase === 'error' ? 'bg-[var(--color-error)]'   :
                    'bg-[var(--color-orange)]',
                  ].join(' ')}
                  style={{ width: `${bulkProgress.total > 0 ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}%` }}
                />
              </div>
            )}
          </th>
        ) : (
          /* ── Normal column headers ── */
          <>
            {columns.map(({ label, field, className = '', title }) => {
              const isActive = sortField === field;
              return (
                <th
                  key={label}
                  title={title}
                  onClick={() => field && onSort(field)}
                  className={[
                    thBase,
                    field ? 'cursor-pointer hover:text-[var(--color-text-primary)] transition-colors' : '',
                    className,
                  ].join(' ')}
                >
                  {label}
                  {field && (
                    <span className={`ml-1 ${isActive ? 'text-[var(--color-orange)]' : 'opacity-30'}`}>
                      {isActive ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  )}
                </th>
              );
            })}
            {/* Empty th for the Details expand column */}
            <th className="w-20" />
          </>
        )}
      </tr>

      {/* ── Select-all-filtered banner row ── */}
      {active && !allSelected && count < filteredCount && (
        <tr className="bg-orange-50 border-b border-orange-100">
          <td colSpan={columns.length + 2} className="px-4 py-1.5 text-xs text-[var(--color-text-secondary)]">
            All <span className="font-semibold">{count}</span> records on this page are selected.{' '}
            <button onClick={onSelectAllFiltered} className="font-semibold text-[var(--color-orange)] hover:underline">
              Select all {filteredCount} filtered records
            </button>
          </td>
        </tr>
      )}
      {active && allSelected && (
        <tr className="bg-orange-50 border-b border-orange-100">
          <td colSpan={columns.length + 2} className="px-4 py-1.5 text-xs text-[var(--color-text-secondary)]">
            All <span className="font-semibold">{filteredCount}</span> filtered records are selected.{' '}
            <button onClick={onClearSelection} className="font-semibold text-[var(--color-orange)] hover:underline">
              Clear selection
            </button>
          </td>
        </tr>
      )}
    </thead>
  );
}

// ── SkeletonRow ───────────────────────────────────────────────────────────────
/**
 * SkeletonRow
 *
 * Animated loading placeholder. Pass the same column widths your real
 * table uses so the skeleton matches the loaded layout exactly.
 *
 * Props:
 *   columns  — array of pixel widths, one per cell (e.g. [80, 120, 160])
 *   checkbox — whether to include a leading checkbox cell (default: true)
 */
export function SkeletonRow({ columns = [], checkbox = true }) {
  return (
    <tr className="border-b border-[var(--color-border)] animate-pulse">
      {checkbox && (
        <td className="px-3 py-3 w-10">
          <div className="h-3 w-3.5 bg-[var(--color-surface-alt)] rounded" />
        </td>
      )}
      {columns.map((w, i) => (
        <td key={i} className="px-3 py-3">
          <div className="h-3 bg-[var(--color-surface-alt)] rounded" style={{ width: w }} />
        </td>
      ))}
    </tr>
  );
}

// ── RecordRow ─────────────────────────────────────────────────────────────────
/**
 * RecordRow
 *
 * Selectable, expandable table row wrapper. Owns the checkbox, selection
 * highlight, hover state, and expand/collapse behavior.
 *
 * Supports two modes:
 *   Uncontrolled (default) — manages its own expanded state internally.
 *   Controlled            — pass `expanded` + `onExpandedChange` to let the
 *                           parent drive expand/collapse (e.g. to open the
 *                           panel programmatically from a sibling element).
 *
 * Props:
 *   id                — unique identifier for this row
 *   selected          — whether this row is currently selected
 *   onToggle          — fn(id) called on checkbox change
 *   expandedContent   — React node rendered full-width below when expanded
 *   colSpan           — total column count (for the expanded row)
 *   children          — <td> elements for this row's data cells
 *   expanded          — (controlled) current expanded state
 *   onExpandedChange  — (controlled) fn(bool) called when expand toggles
 */
export function RecordRow({
  id,
  selected,
  onToggle,
  expandedContent,
  colSpan,
  children,
  expanded: expandedProp,
  onExpandedChange,
}) {
  const [expandedInternal, setExpandedInternal] = useState(false);

  // Controlled if parent passes the expanded prop; uncontrolled otherwise.
  // Think of it like a light switch with an override panel: if the override
  // is plugged in (expandedProp !== undefined), it takes precedence; if not,
  // the switch on the wall (internal state) runs things as normal.
  const isControlled = expandedProp !== undefined;
  const expanded     = isControlled ? expandedProp : expandedInternal;

  function setExpanded(val) {
    if (isControlled) onExpandedChange?.(val);
    else setExpandedInternal(val);
  }

  const hasExpand = Boolean(expandedContent);

  return (
    <>
      <tr
        className={[
          'border-b border-[var(--color-border)] transition-colors',
          selected
            ? 'bg-orange-50 hover:bg-orange-100/70'
            : 'hover:bg-[var(--color-surface)]',
        ].join(' ')}
      >
        <td className="px-3 py-2.5 w-10 align-top">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(id)}
            className="w-3.5 h-3.5 rounded border-[var(--color-border)] accent-[var(--color-orange)] cursor-pointer mt-0.5"
          />
        </td>

        {children}

        {hasExpand && (
          <td className="px-3 py-2 w-20 align-top">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors font-medium"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          </td>
        )}
      </tr>

      {hasExpand && expanded && (
        <tr className="border-b border-[var(--color-border)]">
          <td colSpan={colSpan} className="p-0">
            {expandedContent}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
/**
 * Pagination
 *
 * Page controls with rows-per-page picker.
 */
export function Pagination({
  total,
  page,
  pageSize,
  pageSizeOptions = [25, 50, 100],
  onPage,
  onPageSize,
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1 && total <= pageSizeOptions[0]) return null;

  const btnClass = [
    'px-2.5 py-1 rounded-[var(--radius-md)] text-xs font-medium',
    'text-[var(--color-text-secondary)]',
    'hover:bg-[var(--color-surface-alt)]',
    'disabled:opacity-30 disabled:cursor-not-allowed',
    'transition-colors',
  ].join(' ');

  const from = Math.min((page - 1) * pageSize + 1, total);
  const to   = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--color-border)] bg-white text-xs text-[var(--color-text-secondary)]">
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          value={pageSize}
          onChange={e => { onPageSize(Number(e.target.value)); onPage(1); }}
          className="border border-[var(--color-border)] rounded-[var(--radius-md)] px-2 py-1 text-xs focus:outline-none bg-white"
        >
          {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <span className="mr-2 text-[var(--color-text-muted)]">{from}–{to} of {total}</span>
        <button onClick={() => onPage(1)}          disabled={page === 1}          className={btnClass}>«</button>
        <button onClick={() => onPage(page - 1)}   disabled={page === 1}          className={btnClass}>‹</button>
        <button onClick={() => onPage(page + 1)}   disabled={page === totalPages} className={btnClass}>›</button>
        <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className={btnClass}>»</button>
      </div>
    </div>
  );
}